import React, { useEffect, useState, useRef, lazy, Suspense } from 'react';
import { Data, Layout, Config } from 'plotly.js';

// Lazy load Plot component to reduce initial bundle size
const Plot = lazy(() => import('react-plotly.js'));

interface PlotlyFigure {
    data: Data[];
    layout?: Partial<Layout>;
}

interface PlotlyOrImageProps {
    src: string;
    width?: number;
    height?: number;
    alt?: string;
    onClick?: () => void;
    className?: string;
    style?: React.CSSProperties;
    /**
     * auto: default interactive for plotly
     * static: render plotly as non-interactive (no toolbar)
     * interactive: force interactive
     */
    renderMode?: 'auto' | 'static' | 'interactive';
}

/**
 * Determines if a URL points to a Plotly JSON file by checking the extension
 */
const isPlotlyJson = (url: string): boolean => {
    if (!url) return false;

    // Try to detect via URL pathname (handles presigned URLs with query params)
    try {
        const parsed = new URL(url);
        if (parsed.pathname.toLowerCase().endsWith('.json')) {
            return true;
        }
    } catch {
        // If URL parsing fails, fall back to string checks
    }

    // Fallback: strip query params and check extension
    const withoutQuery = url.split('?')[0].toLowerCase();
    return withoutQuery.endsWith('.json');
};

/**
 * Loading placeholder component
 */
const LoadingPlaceholder: React.FC<{
    width?: number;
    height?: number;
    style?: React.CSSProperties;
    className?: string;
    message?: string;
}> = ({ width, height, style, className, message = 'Loading...' }) => (
    <div
        style={{
            width: width ?? '100%',
            height: height ?? '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            ...style
        }}
        className={className}
    >
        <span>{message}</span>
    </div>
);

/**
 * A component that renders either an image or a Plotly chart
 * based on the source URL type.
 * 
 * Features:
 * - Lazy loads Plotly.js only when needed
 * - Only renders when visible in viewport (IntersectionObserver)
 * - Proper cleanup to prevent memory leaks
 */
const PlotlyOrImage: React.FC<PlotlyOrImageProps> = ({
    src,
    width,
    height,
    alt = 'plot',
    onClick,
    className,
    style,
    renderMode = 'auto'
}) => {
    const [plotlyData, setPlotlyData] = useState<PlotlyFigure | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isVisible, setIsVisible] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    const isJson = isPlotlyJson(src);

    // Intersection Observer to detect visibility
    useEffect(() => {
        const element = containerRef.current;
        if (!element) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsVisible(true);
                    } else {
                        // Unload when scrolled far away to save memory
                        // Works for both images and Plotly charts
                        setIsVisible(false);
                    }
                });
            },
            {
                root: null, // viewport
                rootMargin: '200px', // Load slightly before entering viewport
                threshold: 0
            }
        );

        observer.observe(element);

        return () => {
            observer.disconnect();
        };
    }, []);

    // Fetch Plotly JSON data only when visible
    useEffect(() => {
        // Clear data when not visible to free memory
        if (!isVisible) {
            if (plotlyData) {
                setPlotlyData(null);
            }
            return;
        }

        // Only fetch if it's a JSON file and we have a src
        if (!isJson || !src) {
            return;
        }

        // Abort any previous fetch
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        const fetchPlotlyJson = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const response = await fetch(src, { signal: abortController.signal });
                if (!response.ok) {
                    throw new Error(`Failed to fetch Plotly JSON: ${response.statusText}`);
                }
                const json = await response.json();
                
                // Only set data if not aborted
                if (!abortController.signal.aborted) {
                    setPlotlyData(json);
                }
            } catch (err) {
                // Ignore abort errors
                if (err instanceof Error && err.name === 'AbortError') {
                    return;
                }
                setError(err instanceof Error ? err.message : 'Failed to load chart');
                console.error('Error fetching Plotly JSON:', err);
            } finally {
                if (!abortController.signal.aborted) {
                    setIsLoading(false);
                }
            }
        };

        fetchPlotlyJson();

        // Cleanup function
        return () => {
            abortController.abort();
            abortControllerRef.current = null;
            // Clear plotly data on unmount to free memory
            setPlotlyData(null);
            setIsLoading(false);
            setError(null);
        };
    }, [src, isJson, isVisible]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            // Ensure all state is cleared on unmount
            setPlotlyData(null);
            setIsLoading(false);
            setError(null);
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
        };
    }, []);

    // Container wrapper for intersection observer
    const containerStyle: React.CSSProperties = {
        width: width ?? '100%',
        height: height ?? '100%',
        minHeight: height ?? 100,
        ...style
    };

    // Handle missing src - show fallback placeholder
    if (!src) {
        return (
            <div
                ref={containerRef}
                style={{
                    display: 'inline-block',
                    width: width ?? 100,
                    height: height ?? 100,
                    minWidth: width ?? 100,
                    minHeight: height ?? 100
                }}
            >
                <LoadingPlaceholder
                    width={width}
                    height={height}
                    style={style}
                    className={className}
                    message="No image available"
                />
            </div>
        );
    }

    // Handle image rendering (non-JSON)
    if (!isJson) {
        return (
            <div
                ref={containerRef}
                style={{
                    display: 'inline-block',
                    width: width ?? 'auto',
                    height: height ?? 'auto',
                    minWidth: width ?? 100,
                    minHeight: height ?? 100
                }}
            >
                {isVisible ? (
                    <img
                        src={src}
                        alt={alt}
                        onClick={onClick}
                        className={className}
                        style={{ cursor: onClick ? 'pointer' : 'default', ...style }}
                        width={width}
                        height={height}
                        loading="lazy" // Native lazy loading as fallback
                    />
                ) : (
                    <LoadingPlaceholder
                        width={width}
                        height={height}
                        style={style}
                        className={className}
                        message="Image will load when visible"
                    />
                )}
            </div>
        );
    }

    // Handle Plotly JSON rendering
    return (
        <div ref={containerRef} style={containerStyle}>
            {!isVisible ? (
                // Not visible - show placeholder
                <LoadingPlaceholder
                    width={width}
                    height={height}
                    style={style}
                    className={className}
                    message="Chart will load when visible"
                />
            ) : isLoading ? (
                // Loading state
                <LoadingPlaceholder
                    width={width}
                    height={height}
                    style={style}
                    className={className}
                    message="Loading chart..."
                />
            ) : error ? (
                // Error state
                <div
                    style={{
                        width,
                        height,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#fff5f5',
                        borderRadius: '8px',
                        color: '#c53030',
                        ...style
                    }}
                    className={className}
                >
                    <span>Error: {error}</span>
                </div>
            ) : plotlyData ? (
                // Render Plotly chart with lazy-loaded component
                <Suspense
                    fallback={
                        <LoadingPlaceholder
                            width={width}
                            height={height}
                            style={style}
                            className={className}
                            message="Loading Plotly library..."
                        />
                    }
                >
                    <PlotlyChart
                        plotlyData={plotlyData}
                        width={width}
                        height={height}
                        onClick={onClick}
                        className={className}
                        style={style}
                        renderMode={renderMode}
                    />
                </Suspense>
            ) : (
                // Fallback
                <LoadingPlaceholder
                    width={width}
                    height={height}
                    style={style}
                    className={className}
                />
            )}
        </div>
    );
};

/**
 * Separated Plotly chart component to work with lazy loading
 */
interface PlotlyChartProps {
    plotlyData: PlotlyFigure;
    width?: number;
    height?: number;
    onClick?: () => void;
    className?: string;
    style?: React.CSSProperties;
    renderMode: 'auto' | 'static' | 'interactive';
}

const PlotlyChart: React.FC<PlotlyChartProps> = ({
    plotlyData,
    width,
    height,
    onClick,
    className,
    style,
    renderMode
}) => {
    const plotConfig: Partial<Config> = {
        responsive: true,
        displayModeBar: renderMode !== 'static',
        displaylogo: false,
        staticPlot: renderMode === 'static'
    };

    const staticOverrides: Partial<Layout> =
        renderMode === 'static'
            ? {
                showlegend: false,
                hovermode: false,
                annotations: [],
                xaxis: {
                    ...(plotlyData.layout?.xaxis ?? {}),
                    title: undefined,
                    showticklabels: false,
                    showgrid: false,
                    zeroline: false
                },
                yaxis: {
                    ...(plotlyData.layout?.yaxis ?? {}),
                    title: undefined,
                    showticklabels: false,
                    showgrid: false,
                    zeroline: false
                },
                margin: { l: 20, r: 20, t: plotlyData.layout?.title ? 60 : 20, b: 20 }
            }
            : {};

    const plotLayout: Partial<Layout> = {
        ...plotlyData.layout,
        width: width ?? plotlyData.layout?.width,
        height: height ?? plotlyData.layout?.height,
        autosize: width === undefined || height === undefined,
        margin: plotlyData.layout?.margin || { l: 50, r: 50, t: 50, b: 50 },
        ...staticOverrides
    };

    return (
        <div
            onClick={onClick}
            className={className}
            style={{ cursor: onClick ? 'pointer' : 'default', ...style }}
        >
            <Plot
                data={plotlyData.data}
                layout={plotLayout}
                config={plotConfig}
                style={{ borderRadius: '8px' }}
            />
        </div>
    );
};

export default PlotlyOrImage;
